'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/kit/useTranslation';
import {
  Dialog,
  DialogContent,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatApiError } from '@/services/api/apiHelpers';
import { LocalShipping, CheckCircle, Inventory, LocationOn } from '@mui/icons-material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { TEXT, STATUS, BORDER } from '@/constants/colors';
import useApiCall from '@/services/api/useApiCall';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';

export default function BulkOrderPreviewDialog({ open, onClose, bulkOrderId, initialBulk }) {
  const { t } = useTranslation();
  const { makeApiCall } = useApiCall();

  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(initialBulk || null);
  const [error, setError] = useState(null);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open) return;
    if (!bulkOrderId) return;
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = buildApiConfig(endPointEnums.GET_BULK_ORDER_BY_ID, {
          bulkOrderId,
        });
        const res = await makeApiCall(cfg);
        if (!mounted) return;
        setOrder(res.data || res || null);
      } catch (err) {
        console.error('[BulkOrderPreviewDialog] fetch error', err);
        setError(t('bulkOrder.failedToLoad'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [open, bulkOrderId, makeApiCall]);

  const itemCount = order?.itemCount || (order?.items && order?.items.length) || 0;
  const totalAmount = order?.totalAmount || order?.total || 0;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isSmall}>
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error">{formatApiError(error)}</Typography>
          </Box>
        ) : order ? (
          <Stack spacing={0}>
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                p: { xs: 2, md: 2 },
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.08)',
                }}
              />
              <Stack spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.18)',
                      p: { xs: 0.9, md: 1 },
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Inventory sx={{ fontSize: { xs: 20, md: 22 } }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.72rem', md: '0.78rem' },
                        opacity: 0.9,
                      }}
                    >
                      {t('bulkOrder.bulkOrderLabel')}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.05rem', md: '1.1rem' },
                      }}
                    >
                      {order.bulkOrderReference || 'BLK-000000-000'}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }}>
                  <Chip
                    icon={<LocalShipping sx={{ fontSize: { xs: 14, md: 16 } }} />}
                    label={`${itemCount} ${itemCount === 1 ? 'Item' : 'Items'}`}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.14)',
                      color: 'white',
                      fontWeight: 700,
                      px: { xs: 0.75, md: 1 },
                      py: { xs: 0.35, md: 0.45 },
                      fontSize: { md: '0.9rem' },
                    }}
                    size={isSmall ? 'small' : 'small'}
                  />
                  <Chip
                    icon={<CurrencyRupeeIcon sx={{ fontSize: { xs: 14, md: 16 } }} />}
                    label={`₹${Math.round(totalAmount)}`}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.14)',
                      color: 'white',
                      fontWeight: 700,
                      px: { xs: 0.75, md: 1 },
                      py: { xs: 0.35, md: 0.45 },
                      fontSize: { md: '0.9rem' },
                    }}
                    size={isSmall ? 'small' : 'small'}
                  />
                </Stack>
              </Stack>
            </Box>

            {/* Summary Stats - compact on mobile */}
            <Box
              sx={{
                p: { xs: 1.5, md: 1.5 },
                bgcolor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Paper
                    sx={{
                      p: { xs: 0.9, md: 1 },
                      textAlign: 'center',
                      bgcolor: 'white',
                      border: `1px solid ${BORDER.light}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        color: TEXT.secondary,
                        mb: 0.25,
                      }}
                    >
                      {t('bulkOrder.totalItems')}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.15rem', md: '1.2rem' },
                      }}
                    >
                      {itemCount}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper
                    sx={{
                      p: { xs: 0.9, md: 1 },
                      textAlign: 'center',
                      bgcolor: 'white',
                      border: `1px solid ${BORDER.light}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        color: TEXT.secondary,
                        mb: 0.25,
                      }}
                    >
                      {t('bulkOrder.totalValue')}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.15rem', md: '1.2rem' },
                        color: STATUS.warning,
                      }}
                    >
                      ₹{Math.round(totalAmount)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper
                    sx={{
                      p: { xs: 0.9, md: 1 },
                      textAlign: 'center',
                      bgcolor: 'white',
                      border: `1px solid ${BORDER.light}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        color: TEXT.secondary,
                        mb: 0.25,
                      }}
                    >
                      {t('bulkOrder.avgPrice')}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.15rem', md: '1.2rem' },
                      }}
                    >
                      ₹{itemCount > 0 ? Math.round(totalAmount / itemCount) : 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper
                    sx={{
                      p: { xs: 0.9, md: 1 },
                      textAlign: 'center',
                      bgcolor: 'white',
                      border: `1px solid ${BORDER.light}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        color: TEXT.secondary,
                        mb: 0.25,
                      }}
                    >
                      {t('bulkOrder.statusLabel')}
                    </Typography>
                    <Chip
                      label={t('bulkOrder.active')}
                      size="small"
                      sx={{
                        bgcolor: alpha(STATUS.success, 0.1),
                        color: STATUS.success,
                        fontWeight: 700,
                        mt: 0.4,
                        fontSize: { md: '0.8rem' },
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Items List */}
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontWeight: 800, mb: 1.2, fontSize: '1rem' }}>
                {t('bulkOrder.shipmentItems', { count: itemCount })}
              </Typography>
              <Stack sx={{ gap: 1.5, display: 'flex', flexDirection: 'column' }}>
                {(order.items || []).map((it, idx) => (
                  <Card
                    key={it.item_id || it.itemId || idx}
                    sx={{
                      border: '1px solid #e5e7eb',
                      '&:hover': {
                        boxShadow: { md: '0 4px 12px rgba(0,0,0,0.08)' },
                        borderColor: '#d97706',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ p: { xs: 1, md: 1 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: { xs: 36, md: 36 },
                            height: { xs: 36, md: 36 },
                            borderRadius: 1.5,
                            bgcolor: alpha('#d97706', 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontWeight: 800,
                            color: '#d97706',
                            fontSize: { xs: '0.9rem', md: '1rem' },
                          }}
                        >
                          {idx + 1}
                        </Box>
                        <Stack spacing={0.4} sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: '0.9rem', md: '0.9rem' },
                            }}
                          >
                            {it.recipientName || it.recipient_name || t('bulkOrder.recipient')}
                          </Typography>
                          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                            <LocationOn
                              sx={{
                                fontSize: 13,
                                color: '#6b7280',
                                mt: 0.2,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                color: '#6b7280',
                                fontSize: { xs: '0.78rem', md: '0.8rem' },
                              }}
                            >
                              {typeof it.deliveryAddress === 'object'
                                ? it.deliveryAddress.address ||
                                  it.deliveryAddress.formatted_address ||
                                  JSON.stringify(it.deliveryAddress)
                                : it.deliveryAddress ||
                                  it.delivery_address ||
                                  it.full_address ||
                                  ''}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.75} sx={{ mt: 0.4, flexWrap: 'wrap' }}>
                            <Chip
                              label={
                                (it.parcelType && it.parcelType.join
                                  ? it.parcelType.join(', ')
                                  : it.parcel_type) ||
                                it.category ||
                                'GENERAL'
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.66rem', md: '0.68rem' },
                              }}
                            />
                            <Chip
                              label={`₹${Math.round(it.itemPrice || 0)}`}
                              size="small"
                              sx={{
                                bgcolor: alpha('#d97706', 0.1),
                                color: '#d97706',
                                fontWeight: 700,
                                fontSize: { xs: '0.66rem', md: '0.68rem' },
                              }}
                            />
                          </Stack>
                        </Stack>
                        <CheckCircle
                          sx={{
                            color: '#10b981',
                            fontSize: { xs: 20, md: 24 },
                            flexShrink: 0,
                          }}
                        />
                      </Stack>
                    </Box>
                  </Card>
                ))}
              </Stack>
            </Box>

            {/* Action Buttons */}
            <Divider />
            <Box
              sx={{
                p: 1.25,
                bgcolor: '#f9fafb',
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
              }}
            >
              <Button onClick={onClose} variant="outlined" sx={{ fontWeight: 700, py: 0.75 }}>
                {t('common.close')}
              </Button>
              <Button
                onClick={() => {
                  window.location.href = `/bulk-booking?bulkOrderId=${bulkOrderId}`;
                }}
                variant="contained"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                }}
              >
                {t('bulkOrder.openBulkBooking')}
              </Button>
            </Box>
          </Stack>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography>{t('bulkOrder.noDataAvailable')}</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
